import { UserSound } from "@phosphor-icons/react";
import clsx from "clsx";
import { useCallback, useMemo, useRef, useState } from "react";
import { match } from "ts-pattern";

import { useLocale } from "~/hooks/locale";

import { AudioClue } from "../AudioClue";
import { ImageClue } from "../ImageClue";
import { TextDefinition } from "../TextDefinition";
import type { GameWord } from "./Game";

export const GameClue: React.FC<{
  word: GameWord;
  size?: "sm" | "md" | "lg";
  autoPlay?: boolean;
  isSpeechEnabled?: boolean;
  isImageMatch?: boolean;
}> = ({
  word,
  autoPlay = true,
  isSpeechEnabled = true,
  isImageMatch,
  size = "md",
}) => {
  const { t } = useLocale();

  const definitionRef = useRef<HTMLDivElement>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const isSpeechAvailable = useMemo(
    () => "speechSynthesis" in window && "SpeechSynthesisUtterance" in window,
    [],
  );

  const speak = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.stopPropagation();

      const utterance = new SpeechSynthesisUtterance(
        definitionRef.current?.innerText,
      );

      utterance.addEventListener("end", () => {
        setIsSpeaking(false);
      });

      utterance.addEventListener("start", () => {
        setIsSpeaking(true);
      });

      speechSynthesis.cancel();
      speechSynthesis.speak(utterance);
    },
    [],
  );

  const audioClue = word.definitions.find((definition) => definition.audio);

  if (audioClue?.audio) {
    return <AudioClue autoPlay={autoPlay} src={audioClue.audio} />;
  }

  const imageClue = word.definitions.find((definition) => definition.image);

  if (imageClue) {
    // if it's an image clue, and we're in image match mode, show the solution
    return isImageMatch ? (
      <div
        className={clsx(
          "font-medium",
          match(size)
            .with("sm", () => "p-3")
            .with("md", "lg", (size) =>
              clsx(
                "p-4",
                match(size)
                  .with("md", () => "text-2xl md:text-3xl")
                  .with("lg", () => "text-3xl sm:text-4xl md:text-5xl md:p-6")
                  .exhaustive(),
              ),
            )
            .exhaustive(),
        )}
      >
        {word.solution}
      </div>
    ) : (
      <ImageClue
        key={imageClue.id}
        className={match(size)
          .with("sm", () => "gap-3 p-3 text-lg md:text-xl")
          .with("md", "lg", (size) =>
            clsx(
              "gap-4 p-4",
              match(size)
                .with("md", () => "text-2xl md:text-3xl")
                .with("lg", () => "text-3xl sm:text-4xl md:text-5xl md:p-6")
                .exhaustive(),
            ),
          )
          .exhaustive()}
        definition={imageClue}
      />
    );
  }

  return (
    <>
      <div
        className={match(size)
          .with("sm", () => "p-3")
          .with("md", "lg", (size) =>
            clsx(
              "p-4",
              match(size)
                .with("md", () => "text-lg md:text-xl")
                .with("lg", () => "text-xl sm:text-2xl md:text-3xl md:p-6")
                .exhaustive(),
              size === "lg" && "md:p-6",
            ),
          )
          .exhaustive()}
        ref={definitionRef}
      >
        {word.definitions[0] && (
          <TextDefinition definition={word.definitions[0]} />
        )}
      </div>
      {isSpeechAvailable && isSpeechEnabled && (
        <button
          className={clsx(
            "btn btn-xs absolute bottom-2 right-2",
            size === "sm" && "btn-square",
          )}
          onClick={speak}
        >
          {isSpeaking ? (
            <div className="loading loading-bars loading-xs" />
          ) : (
            <UserSound className="size-4" weight="bold" />
          )}
          {size !== "sm" && t("speak")}
        </button>
      )}
    </>
  );
};
